import React, { useState, useEffect, useCallback } from "react";
import {
  Facebook,
  MessageSquare,
  ThumbsUp,
  Share2,
  ExternalLink,
  ChevronLeft,
  Send,
  Loader2,
  Plus,
  X,
  Trash2,
  RefreshCw,
  AlertCircle,
  Check,
  Calendar,
  Clock,
  Edit2,
} from "lucide-react";
import {
  fetchFacebookPosts,
  createFacebookPost,
  fetchFacebookComments,
  createFacebookComment,
  replyToFacebookComment,
  hideFacebookComment,
  deleteFacebookComment,
  createScheduledPost,
  fetchScheduledPosts,
  updateScheduledPost,
  deleteScheduledPost,
} from "../api/socialMedia";
import { fetchClaw9Accounts } from "../api/claw9";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

const formatScheduledDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const STATUS_COLORS = {
  draft: "bg-gray-500/20 text-gray-400",
  scheduled: "bg-blue-500/20 text-blue-400",
  publishing: "bg-yellow-500/20 text-yellow-400",
  published: "bg-emerald-500/20 text-emerald-400",
  failed: "bg-red-500/20 text-red-400",
};

const StatusBadge = ({ status }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${STATUS_COLORS[status] || STATUS_COLORS.draft}`}>
    {status}
  </span>
);

const FacebookPostsDashboard = () => {
  const [accountId, setAccountId] = useState(null);
  const [integrationId, setIntegrationId] = useState(null);
  const [activeTab, setActiveTab] = useState("published"); // "published" | "scheduled"
  const [posts, setPosts] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [newComment, setNewComment] = useState("");
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [newPostText, setNewPostText] = useState("");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [postMode, setPostMode] = useState("now"); // "now" | "schedule"
  const [posting, setPosting] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);

  // Initialize
  useEffect(() => {
    const init = async () => {
      try {
        const result = await fetchClaw9Accounts();
        const accounts = result?.accounts || [];
        if (accounts.length > 0) {
          const account = accounts[0];
          setAccountId(account.account_id);
          // Find Facebook integration for scheduled posts
          const fbIntegration = account.integrations?.find(
            (i) => i.platform === "facebook" && i.status === "active"
          );
          if (fbIntegration) {
            setIntegrationId(fbIntegration.integration_id);
          }
        }
      } catch (err) {
        setError("Failed to load account");
      }
    };
    init();
  }, []);

  // Load posts
  const loadPosts = useCallback(async (loadMore = false) => {
    if (!accountId) return;
    setLoading(!loadMore);

    try {
      const result = await fetchFacebookPosts(accountId, {
        after: loadMore ? cursor : null
      });
      const newPosts = result.posts || [];

      setPosts(prev => loadMore ? [...prev, ...newPosts] : newPosts);
      setCursor(result.next_cursor);
      setHasMore(result.has_more);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accountId, cursor]);

  useEffect(() => {
    if (accountId) loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // Load scheduled posts
  const loadScheduledPosts = useCallback(async () => {
    if (!integrationId) return;
    setLoadingScheduled(true);

    try {
      const result = await fetchScheduledPosts({ integrationId });
      setScheduledPosts(result.posts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingScheduled(false);
    }
  }, [integrationId]);

  useEffect(() => {
    if (integrationId && activeTab === "scheduled") loadScheduledPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, activeTab]);

  // Load comments
  const loadComments = async (post) => {
    setSelectedPost(post);
    setLoadingComments(true);
    setComments([]);

    try {
      const result = await fetchFacebookComments(accountId, post.id);
      setComments(result.comments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingComments(false);
    }
  };

  // Create or schedule post
  const handleCreatePost = async () => {
    if (!newPostText.trim()) return;
    setPosting(true);

    try {
      if (postMode === "schedule" && scheduleDate && scheduleTime) {
        // Create scheduled post
        const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
        await createScheduledPost({
          integrationId,
          content: newPostText,
          scheduledAt,
          status: "scheduled",
        });
        setSuccess("Post scheduled!");
        loadScheduledPosts();
      } else {
        // Post immediately
        await createFacebookPost(accountId, { message: newPostText });
        setSuccess("Post published!");
        loadPosts();
      }
      resetPostForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  // Update scheduled post
  const handleUpdatePost = async () => {
    if (!editingPost || !newPostText.trim()) return;
    setPosting(true);

    try {
      const scheduledAt = scheduleDate && scheduleTime
        ? new Date(`${scheduleDate}T${scheduleTime}`).toISOString()
        : editingPost.scheduled_at;

      await updateScheduledPost(editingPost.post_id, {
        content: newPostText,
        scheduledAt,
      });
      setSuccess("Post updated!");
      loadScheduledPosts();
      resetPostForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  // Delete scheduled post
  const handleDeleteScheduledPost = async (postId) => {
    if (!window.confirm("Delete this scheduled post?")) return;

    try {
      await deleteScheduledPost(postId);
      setSuccess("Post deleted");
      loadScheduledPosts();
    } catch (err) {
      setError(err.message);
    }
  };

  // Reset post form
  const resetPostForm = () => {
    setNewPostText("");
    setScheduleDate("");
    setScheduleTime("");
    setPostMode("now");
    setShowCreatePost(false);
    setEditingPost(null);
  };

  // Open edit modal
  const handleEditPost = (post) => {
    setEditingPost(post);
    setNewPostText(post.content || "");
    if (post.scheduled_at) {
      const d = new Date(post.scheduled_at);
      setScheduleDate(d.toISOString().split("T")[0]);
      setScheduleTime(d.toTimeString().slice(0, 5));
      setPostMode("schedule");
    }
    setShowCreatePost(true);
  };

  // Post comment
  const handlePostComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);

    try {
      await createFacebookComment(accountId, selectedPost.id, newComment);
      setNewComment("");
      setSuccess("Comment posted!");
      loadComments(selectedPost);
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  // Reply to comment
  const handleReply = async (commentId) => {
    if (!replyText.trim()) return;
    setPosting(true);

    try {
      await replyToFacebookComment(accountId, commentId, replyText);
      setReplyText("");
      setReplyingTo(null);
      setSuccess("Reply sent!");
      loadComments(selectedPost);
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  // Hide comment
  const handleHide = async (comment) => {
    try {
      await hideFacebookComment(accountId, comment.id, !comment.is_hidden);
      setSuccess(comment.is_hidden ? "Comment visible" : "Comment hidden");
      loadComments(selectedPost);
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete comment
  const handleDelete = async (comment) => {
    if (!window.confirm("Delete this comment?")) return;

    try {
      await deleteFacebookComment(accountId, comment.id);
      setSuccess("Comment deleted");
      loadComments(selectedPost);
    } catch (err) {
      setError(err.message);
    }
  };

  // Auto-clear messages
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(t);
    }
  }, [success]);

  // Loading state
  if (loading && !posts.length) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#ff4d4d]" />
      </div>
    );
  }

  // Comments view
  if (selectedPost) {
    return (
      <div className="h-full flex flex-col bg-[#0a0f1c] rounded-2xl border border-white/10">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-white/10">
          <button onClick={() => setSelectedPost(null)} className="p-1.5 hover:bg-white/10 rounded-lg">
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <span className="font-medium text-white">Comments</span>
          <span className="text-white/40 text-sm">({comments.length})</span>
        </div>

        {/* Alerts */}
        {success && (
          <div className="mx-4 mt-3 p-2.5 bg-emerald-500/10 text-emerald-400 text-sm rounded-lg flex items-center gap-2">
            <Check className="w-4 h-4" /> {success}
          </div>
        )}

        {/* Comments */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loadingComments ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-white/30" />
            </div>
          ) : comments.length === 0 ? (
            <p className="text-center text-white/40 py-8">No comments yet</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="group">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#1877f2]/20 flex items-center justify-center flex-shrink-0">
                    <Facebook className="w-4 h-4 text-[#4d94ff]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-medium text-white text-sm">{comment.from_name || "User"}</span>
                      <span className="text-xs text-white/40">{formatDate(comment.created_time)}</span>
                    </div>
                    <p className="text-white/70 text-sm mt-0.5">{comment.message}</p>

                    {/* Actions */}
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <span className="text-white/40">{comment.like_count || 0} likes</span>
                      <button
                        onClick={() => setReplyingTo(comment.id)}
                        className="text-[#ff4d4d] hover:text-[#ff6666]"
                      >
                        Reply
                      </button>
                      <button
                        onClick={() => handleHide(comment)}
                        className="text-white/40 hover:text-white/70 opacity-0 group-hover:opacity-100"
                      >
                        {comment.is_hidden ? "Unhide" : "Hide"}
                      </button>
                      <button
                        onClick={() => handleDelete(comment)}
                        className="text-white/40 hover:text-red-400 opacity-0 group-hover:opacity-100"
                      >
                        Delete
                      </button>
                    </div>

                    {/* Reply input */}
                    {replyingTo === comment.id && (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="text"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Write a reply..."
                          className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]"
                          autoFocus
                          onKeyDown={(e) => e.key === "Enter" && handleReply(comment.id)}
                        />
                        <button
                          onClick={() => handleReply(comment.id)}
                          disabled={posting}
                          className="px-3 py-2 bg-[#ff4d4d] text-white rounded-lg text-sm disabled:opacity-50"
                        >
                          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                        <button onClick={() => setReplyingTo(null)} className="p-2 text-white/40 hover:text-white">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Comment input */}
        <div className="p-4 border-t border-white/10">
          <div className="flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment..."
              className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]"
              onKeyDown={(e) => e.key === "Enter" && handlePostComment()}
            />
            <button
              onClick={handlePostComment}
              disabled={posting || !newComment.trim()}
              className="px-4 py-2.5 bg-[#ff4d4d] text-white rounded-lg disabled:opacity-50"
            >
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Posts list view
  return (
    <div className="h-full flex flex-col bg-[#0a0f1c] rounded-2xl border border-white/10">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1877f2]/20 flex items-center justify-center">
            <Facebook className="w-5 h-5 text-[#4d94ff]" />
          </div>
          <div>
            <h1 className="font-semibold text-white">Facebook Posts</h1>
            <p className="text-xs text-white/40">
              {activeTab === "published" ? `${posts.length} published` : `${scheduledPosts.length} scheduled`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => activeTab === "published" ? loadPosts() : loadScheduledPosts()}
            className="p-2.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 text-white/70 ${loading || loadingScheduled ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => setShowCreatePost(true)}
            className="px-4 py-2 bg-[#ff4d4d] hover:bg-[#ff6666] text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New Post
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10">
        <button
          onClick={() => setActiveTab("published")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === "published"
              ? "text-[#ff4d4d] border-b-2 border-[#ff4d4d]"
              : "text-white/50 hover:text-white/70"
          }`}
        >
          Published
        </button>
        <button
          onClick={() => setActiveTab("scheduled")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === "scheduled"
              ? "text-[#ff4d4d] border-b-2 border-[#ff4d4d]"
              : "text-white/50 hover:text-white/70"
          }`}
        >
          Scheduled
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mx-4 mt-3 p-2.5 bg-red-500/10 text-red-400 text-sm rounded-lg flex items-center justify-between">
          <span className="flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</span>
          <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="mx-4 mt-3 p-2.5 bg-emerald-500/10 text-emerald-400 text-sm rounded-lg flex items-center gap-2">
          <Check className="w-4 h-4" /> {success}
        </div>
      )}

      {/* Posts */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {activeTab === "published" ? (
          /* Published Posts */
          posts.length === 0 ? (
            <div className="text-center py-12 text-white/40">
              <Facebook className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No posts yet</p>
            </div>
          ) : (
            <>
              {posts.map((post) => (
                <div key={post.id} className="p-4 bg-white/[0.02] hover:bg-white/[0.04] border border-white/5 rounded-xl transition-colors">
                  {/* Post content */}
                  <p className="text-white/80 text-sm line-clamp-3">{post.message || "(No text)"}</p>

                  {/* Post image */}
                  {post.full_picture && (
                    <img src={post.full_picture} alt="" className="mt-3 rounded-lg max-h-48 object-cover" />
                  )}

                  {/* Meta */}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-4 text-xs text-white/40">
                      <span className="flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" /> {post.likes_count || 0}</span>
                      <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {post.comments_count || 0}</span>
                      <span className="flex items-center gap-1"><Share2 className="w-3.5 h-3.5" /> {post.shares || 0}</span>
                      <span>{formatDate(post.created_time)}</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => loadComments(post)}
                        className="px-3 py-1.5 text-xs text-[#ff4d4d] hover:bg-[#ff4d4d]/10 rounded-lg"
                      >
                        Comments
                      </button>
                      {post.permalink_url && (
                        <a
                          href={post.permalink_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-white/40 hover:text-white/70 hover:bg-white/10 rounded-lg"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {hasMore && (
                <button
                  onClick={() => loadPosts(true)}
                  disabled={loading}
                  className="w-full py-3 text-sm text-white/50 hover:text-white/70"
                >
                  {loading ? "Loading..." : "Load more"}
                </button>
              )}
            </>
          )
        ) : (
          /* Scheduled Posts */
          loadingScheduled ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-white/30" />
            </div>
          ) : scheduledPosts.length === 0 ? (
            <div className="text-center py-12 text-white/40">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No scheduled posts</p>
              <button
                onClick={() => {
                  setPostMode("schedule");
                  setShowCreatePost(true);
                }}
                className="mt-3 text-[#ff4d4d] hover:text-[#ff6666] text-sm"
              >
                Schedule your first post
              </button>
            </div>
          ) : (
            scheduledPosts.map((post) => (
              <div key={post.post_id} className="p-4 bg-white/[0.02] hover:bg-white/[0.04] border border-white/5 rounded-xl transition-colors">
                {/* Status badge */}
                <div className="flex items-center justify-between mb-2">
                  <StatusBadge status={post.status} />
                  <div className="flex items-center gap-1 text-xs text-white/40">
                    <Clock className="w-3.5 h-3.5" />
                    {formatScheduledDate(post.scheduled_at)}
                  </div>
                </div>

                {/* Post content */}
                <p className="text-white/80 text-sm line-clamp-3">{post.content || "(No text)"}</p>

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 mt-3">
                  {(post.status === "draft" || post.status === "scheduled") && (
                    <>
                      <button
                        onClick={() => handleEditPost(post)}
                        className="p-1.5 text-white/40 hover:text-white/70 hover:bg-white/10 rounded-lg"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteScheduledPost(post.post_id)}
                        className="p-1.5 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {post.status === "failed" && post.error_message && (
                    <span className="text-xs text-red-400">{post.error_message}</span>
                  )}
                </div>
              </div>
            ))
          )
        )}
      </div>

      {/* Create/Edit Post Modal */}
      {showCreatePost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="w-full max-w-md bg-[#0d1424] border border-white/10 rounded-2xl">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-medium text-white">
                {editingPost ? "Edit Post" : "Create Post"}
              </h3>
              <button onClick={resetPostForm} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <textarea
                value={newPostText}
                onChange={(e) => setNewPostText(e.target.value)}
                placeholder="What's on your mind?"
                rows={4}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] resize-none"
                autoFocus
              />

              {/* Post timing options */}
              {!editingPost && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setPostMode("now")}
                    className={`flex-1 py-2 text-sm rounded-lg transition-colors ${
                      postMode === "now"
                        ? "bg-[#ff4d4d]/20 text-[#ff4d4d] border border-[#ff4d4d]/30"
                        : "bg-white/5 text-white/50 border border-white/10 hover:text-white/70"
                    }`}
                  >
                    Post Now
                  </button>
                  <button
                    onClick={() => setPostMode("schedule")}
                    className={`flex-1 py-2 text-sm rounded-lg transition-colors flex items-center justify-center gap-2 ${
                      postMode === "schedule"
                        ? "bg-[#ff4d4d]/20 text-[#ff4d4d] border border-[#ff4d4d]/30"
                        : "bg-white/5 text-white/50 border border-white/10 hover:text-white/70"
                    }`}
                  >
                    <Calendar className="w-4 h-4" /> Schedule
                  </button>
                </div>
              )}

              {/* Schedule date/time picker */}
              {(postMode === "schedule" || editingPost) && (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-white/50 mb-1">Date</label>
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#ff4d4d] [color-scheme:dark]"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-white/50 mb-1">Time</label>
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#ff4d4d] [color-scheme:dark]"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-white/10">
              <button
                onClick={resetPostForm}
                className="px-4 py-2 text-white/60 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={editingPost ? handleUpdatePost : handleCreatePost}
                disabled={posting || !newPostText.trim() || (postMode === "schedule" && (!scheduleDate || !scheduleTime))}
                className="px-4 py-2 bg-[#ff4d4d] text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {posting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : postMode === "schedule" ? (
                  <Calendar className="w-4 h-4" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {editingPost ? "Update" : postMode === "schedule" ? "Schedule" : "Post"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacebookPostsDashboard;
